import { LockKey } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import type { UseQueryResult } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Avatar, Badge, EmptyState, InlineError } from "@/components/ui";
import { loc } from "@/lib/format";
import type { Localized, RoleScope } from "./api";
import { adminError, isForbidden } from "./errors";

/** Avatar with name and email stacked, the way a person appears in every admin table. */
export function UserCell({
  name,
  email,
  seed,
  bot,
  disabled,
  you,
  size = "md",
}: {
  name: string;
  email?: string | null;
  seed?: string;
  bot?: boolean;
  disabled?: boolean;
  you?: boolean;
  size?: "md" | "lg";
}) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <Avatar name={name} seed={seed} bot={bot} size={size} labelled={false} />
      <div className="flex min-w-0 flex-col">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate-title font-medium text-fg" title={name}>
            {name}
          </span>
          {you ? <span className="shrink-0 text-xs text-fg-subtle">{m.ui_you_suffix()}</span> : null}
          {disabled ? <Badge tone="warning">{m.adm_status_disabled()}</Badge> : null}
        </span>
        {email ? (
          <span className="truncate-title text-xs text-fg-muted" title={email}>
            {email}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export const roleName = (r: { name: Localized } | Localized): string => loc("name" in r ? (r.name as Localized) : r);

/** "Whole instance", "Space Acme Shop", "Product Web Store". */
export function scopeText(scope: RoleScope, name?: string | null): string {
  if (scope === "instance") return m.adm_scope_instance();
  return scope === "space"
    ? m.adm_scope_space_named({ name: name ?? m.adm_scope_unknown() })
    : m.adm_scope_product_named({ name: name ?? m.adm_scope_unknown() });
}

/**
 * Loading, error and permission-denied handling for one admin query. Cold load shows `skeleton`,
 * a 403 says what is missing, any other failure is inline with retry. Warm data renders at once.
 */
export function QueryBoundary<T>({
  query,
  skeleton,
  needs,
  children,
}: {
  query: UseQueryResult<T>;
  skeleton: ReactNode;
  /** The permission the section needs, named for the denied message. */
  needs?: string;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <>{skeleton}</>;
  if (query.isError) {
    if (isForbidden(query.error)) {
      return (
        <EmptyState
          icon={LockKey}
          title={m.adm_section_denied_title()}
          description={needs ? m.adm_section_denied_body({ permission: needs }) : m.adm_denied_body()}
        />
      );
    }
    return (
      <InlineError
        message={adminError(query.error)}
        onRetry={() => void query.refetch()}
        isRetrying={query.isFetching}
      />
    );
  }
  return <>{children(query.data)}</>;
}

/** `?name=<id>` in the URL opens a detail drawer: deep-linkable, Back closes it. */
export function useDrawerParam(name: string): [string | null, (id: string | null) => void] {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const raw = search[name];
  const value = typeof raw === "string" || typeof raw === "number" ? String(raw) : null;
  const set = (id: string | null) =>
    void navigate({
      to: ".",
      search: ((prev: Record<string, unknown>) => ({ ...prev, [name]: id ?? undefined })) as never,
      replace: id === null,
    });
  return [value, set];
}

/** Search-param validator for routes that own a drawer param. */
export const drawerSearch =
  <K extends string>(name: K) =>
  (s: Record<string, unknown>): { [P in K]?: string } =>
    ({ [name]: typeof s[name] === "string" || typeof s[name] === "number" ? String(s[name]) : undefined }) as {
      [P in K]?: string;
    };

/** Count with its own singular message (`key_one`) and a plural one (`key`): "1 member", "3 members". */
export function plural(
  n: number,
  one: (p: { count: number }) => string,
  many: (p: { count: number }) => string,
): string {
  return (n === 1 ? one : many)({ count: n });
}

/** Scope inside a sentence: "the whole instance", "the space Acme", "the product Web" (German: accusative). */
export function whereText(scope: RoleScope, name?: string | null): string {
  if (scope === "instance") return m.adm_where_instance();
  const n = name ?? m.adm_scope_unknown();
  return scope === "space" ? m.adm_where_space({ name: n }) : m.adm_where_product({ name: n });
}
